import { __awaiter } from "tslib";
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, useTheme } from '../../../theme';
import { AggregatorInsuranceGroups } from '@entities/insurance';
import { useInsuranceGroupTexts, useInsuranceUpsale } from './hooks';
import { isAlwaysOpenInsurancePopup } from './utils';
import { useIataCode } from '../../../hooks';
import { Money } from '../../../Money';
import { useRenderers } from '../../../renderProps';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import cn from 'classnames';
import { AdditionalServiceType } from '../../types';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { InsurancePageModal, InsuranceUpsaleModal } from '../../../InsurancePage';
import SimpleLoader from '../../../SimpleLoader';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { ModalX } from '../../../BaseComponents';
const InsuranceUpsaleAggregator = ({ insurancePrograms, groupName, segmentId }) => {
    var _a;
    const { t } = useTranslation('Checkout');
    const { InsuranceUpsale: theme, Insurance: cardStyles } = useTheme('CheckoutAdditionalService');
    const iataCode = useIataCode();
    const model = useInsuranceUpsale(insurancePrograms);
    const groupTexts = useInsuranceGroupTexts(groupName, insurancePrograms);
    const onServiceDelete = (_, service) => __awaiter(void 0, void 0, void 0, function* () {
        const insuranceService = service;
        yield model.onDeleteInsurance(insuranceService.code);
    });
    const renderPriceDescription = (isSelected) => {
        return isSelected ? (model.totalPrice.amount > 0 && (React.createElement("div", { className: theme.price },
            React.createElement("span", { className: theme.price__label }, t('Total')),
            React.createElement(Money, { moneyClassName: theme.money, money: model.totalPrice }),
            React.createElement(CdnIcon, { icon: "Info/Item Icluded", className: theme.includedIcon })))) : (React.createElement(React.Fragment, null,
            React.createElement("span", { className: theme.forAll }, t('For all passengers')),
            React.createElement("div", { className: theme.price },
                model.isDifferentPrices && React.createElement("span", { className: theme.price__label }, t('from')),
                React.createElement(Money, { moneyClassName: theme.money, money: model.minPrice }))));
    };
    const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
    const ServiceWrapper = (_a = CustomCheckoutAdditionalService === null || CustomCheckoutAdditionalService === void 0 ? void 0 : CustomCheckoutAdditionalService.CheckoutAdditionalService) !== null && _a !== void 0 ? _a : CheckoutAdditionalService;
    const isSingleInsurance = model.singleInsurance && !isAlwaysOpenInsurancePopup(iataCode);
    const onAddClick = isSingleInsurance ? model.onAddSingle : () => model.open();
    const buttons = React.useMemo(() => {
        return [
            {
                text: model.singleInsurance ? t('Remove protection') : t('Change'),
                action: model.singleInsurance ? model.onDeleteSingle : model.open,
                className: cn({
                    [theme.loading]: model.isLoading
                })
            }
        ];
    }, [model.isLoading, model.singleInsurance]);
    return (React.createElement(React.Fragment, null,
        React.createElement(InsuranceSummary, { onServiceDelete: model.totalPrice.amount > 0 ? onServiceDelete : null, selectedInsurances: model.selectedPrograms, isConfirmed: model.isConfirmed, isDownload: true, mode: "aggregator" }, items => {
            const isSelected = items.length > 0;
            return (React.createElement(ThemeProvider, { value: {
                    ServiceSummary: {
                        ServiceSummaryOption: {
                            option: cardStyles.selectedOption,
                            option__name: cardStyles.option__name
                        }
                    }
                } },
                React.createElement(ServiceWrapper, { header: groupTexts.title, renderHeaderPrice: () => renderPriceDescription(isSelected), serviceType: AdditionalServiceType.Insurance, className: cardStyles.insurance, coverClassName: cardStyles.insurance_aggregator, isLoading: model.isLoading, isSelected: isSelected, priceFrom: model.minPrice, priceTotal: model.totalPrice, hideFooterButtons: !model.canModify, aggregationIcons: [
                        React.createElement(CdnIcon, { icon: "Services/Insurance" }),
                        React.createElement(CdnIcon, { icon: "Payment/Cash" })
                    ], mode: "aggregator", description: React.createElement("div", null,
                        React.createElement(SafeHtml, { tag: "div", html: groupTexts.descr }),
                        React.createElement("ul", null, groupTexts.options.map(option => (React.createElement(SafeHtml, { key: option, tag: "li", html: option }))))), addClick: onAddClick, buttons: buttons },
                    React.createElement(ServiceSummaryDivider, null,
                        React.createElement(AggregatorInsuranceGroups, { items: items, programs: insurancePrograms, segmentId: segmentId })))));
        }),
        React.createElement(ModalX, { classes: { scrollBody: theme.scrollBody, paper: theme.paper }, isOpen: model.isOpen, size: "fullscreen" },
            React.createElement(InsurancePageModal, { availablePrograms: model.availablePrograms, onServicesChanged: model.setIsNeedToSendSaveReq, onTotalInsurancePriceChange: model.setInsurancePriceToPay, onSaveOrderServicesParamsChange: model.setSaveOrderServicesParams, onDownloadNasInsurance: model.onDownloadVskInsurance, totalPrice: model.insurancePriceToPay, onConfirm: model.onConfirm, onClose: model.onClose }),
            model.isLoading && React.createElement(SimpleLoader, null)),
        React.createElement(InsuranceUpsaleModal, { insurance: model.priorityInsurance, isOpen: model.isUpsaleOpen, onAgree: model.handleAgree, onDisagree: model.handleDisagree })));
};
export default InsuranceUpsaleAggregator;
